import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#00695c';
const PRIMARY_MID = '#00897b';
const PRIMARY_LT  = '#e0f2f1';

const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};
const fmtDate = (d) => {
    if (!d) return '-';
    const p = new Date(d);
    if (isNaN(p)) return d;
    return `${String(p.getDate()).padStart(2,'0')}/${String(p.getMonth()+1).padStart(2,'0')}/${p.getFullYear()}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const DailyItemMovement = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate]   = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]       = useState(fmt2Date(today));
    const [items, setItems]         = useState([]);
    const [itemId, setItemId]       = useState('0');
    const [data, setData]           = useState([]);
    const [loading, setLoading]     = useState(false);
    const [error, setError]         = useState('');
    const [fetched, setFetched]     = useState(false);
    const [search, setSearch]       = useState('');

    useEffect(() => {
        if (!companyId || !token) return;
        fetchData('ItemApi', `/${companyId}`, token)
            .then(res => setItems(res?.response_data || res || []))
            .catch(() => setItems([]));
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                setData([]);
                setFetched(false);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token, itemId]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'InventoryReportApi',
                `/DailyItemMovement/${fromDate}/${toDate}/${companyId}/${branchId}/${itemId || 0}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch daily item movement');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.ItemName || '').toLowerCase().includes(q) ||
            (r.Code || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        PurchaseQty:       acc.PurchaseQty       + (parseFloat(r.PurchaseQty)       || 0),
        PurchaseReturnQty: acc.PurchaseReturnQty + (parseFloat(r.PurchaseReturnQty) || 0),
        SaleQty:           acc.SaleQty           + (parseFloat(r.SaleQty)           || 0),
        SaleReturnQty:     acc.SaleReturnQty     + (parseFloat(r.SaleReturnQty)     || 0),
        StockInQty:        acc.StockInQty        + (parseFloat(r.StockInQty)        || 0),
        StockOutQty:       acc.StockOutQty       + (parseFloat(r.StockOutQty)       || 0),
        NetQty:            acc.NetQty            + (parseFloat(r.NetQty)            || 0),
    }), { PurchaseQty: 0, PurchaseReturnQty: 0, SaleQty: 0, SaleReturnQty: 0, StockInQty: 0, StockOutQty: 0, NetQty: 0 }), [filtered]);

    const handleExport = async () => {
        if (!filtered.length) return;
        const headers = ['#', 'Date', 'Code', 'Item Name', 'Purchase', 'Pur.Return', 'Sale', 'Sale Return', 'Stock In', 'Stock Out', 'Net Qty'];
        const rows = filtered.map((r, i) => [
            i + 1, fmtDate(r.TranDate), r.Code, r.ItemName,
            r.PurchaseQty, r.PurchaseReturnQty, r.SaleQty, r.SaleReturnQty,
            r.StockInQty, r.StockOutQty, r.NetQty,
        ]);

        await exportStyledExcel({
            reportTitle: 'DAILY ITEM MOVEMENT REPORT',
            sheetName: 'Daily Item Movement',
            fileName: `DailyItemMovement_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', 'Totals',
                fmtQ(totals.PurchaseQty), fmtQ(totals.PurchaseReturnQty),
                fmtQ(totals.SaleQty), fmtQ(totals.SaleReturnQty),
                fmtQ(totals.StockInQty), fmtQ(totals.StockOutQty), fmtQ(totals.NetQty)],
            columnWidths: [4, 12, 10, 26, 12, 12, 12, 12, 10, 10, 12],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #4db6ac', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading daily item movement..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Daily Item Movement
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Inventory Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <SearchableDropdown
                        value={itemId}
                        onChange={v => setItemId(v)}
                        options={[{ value: '0', label: 'All Items' }, ...items.map(it => ({ value: String(it.Id), label: `${it.Code} - ${it.Name}` }))]}
                        placeholder="All Items"
                        minWidth={200}
                        primaryColor={PRIMARY}
                    />
                    <input placeholder="Search item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Records',    value: filtered.length,                  color: PRIMARY },
                            { label: 'Purchase',   value: fmtQ(totals.PurchaseQty),         color: '#059669' },
                            { label: 'Sale',       value: fmtQ(totals.SaleQty),             color: '#0277bd' },
                            { label: 'Stock In',   value: fmtQ(totals.StockInQty),          color: '#7c3aed' },
                            { label: 'Stock Out',  value: fmtQ(totals.StockOutQty),         color: '#dc2626' },
                            { label: 'Net Qty',    value: fmtQ(totals.NetQty),              color: totals.NetQty >= 0 ? PRIMARY : '#dc2626' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} records
                        {search && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 1000, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Date</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 200 }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Purchase</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Pur.Return</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sale</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sale Return</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Stock In</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Stock Out</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#004d40' }}>Net Qty</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => {
                                const net = parseFloat(r.NetQty) || 0;
                                return (
                                    <tr key={`${r.ItemId}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                        <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                        <td style={{ ...tdBase, color: '#374151' }}>{fmtDate(r.TranDate)}</td>
                                        <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>
                                            <span style={{ color: '#64748b', fontSize: 10, marginRight: 4 }}>{r.Code}</span>{r.ItemName || '-'}
                                        </td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#059669' }}>{parseFloat(r.PurchaseQty) ? fmtQ(r.PurchaseQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#f59e0b' }}>{parseFloat(r.PurchaseReturnQty) ? fmtQ(r.PurchaseReturnQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 600 }}>{parseFloat(r.SaleQty) ? fmtQ(r.SaleQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626' }}>{parseFloat(r.SaleReturnQty) ? fmtQ(r.SaleReturnQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#7c3aed' }}>{parseFloat(r.StockInQty) ? fmtQ(r.StockInQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626' }}>{parseFloat(r.StockOutQty) ? fmtQ(r.StockOutQty) : '-'}</td>
                                        <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: net >= 0 ? PRIMARY : '#dc2626' }}>{fmtQ(r.NetQty)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700 }}>
                                    <td colSpan={3} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 700 }}>{fmtQ(totals.PurchaseQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#f59e0b', fontWeight: 700 }}>{fmtQ(totals.PurchaseReturnQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(totals.SaleQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtQ(totals.SaleReturnQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#7c3aed', fontWeight: 700 }}>{fmtQ(totals.StockInQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>{fmtQ(totals.StockOutQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: totals.NetQty >= 0 ? PRIMARY : '#dc2626' }}>{fmtQ(totals.NetQty)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default DailyItemMovement;
